import React, { useContext, useEffect, useState } from "react";
import { Badge, Box, Button, Card, Cell, Heading, Layout, Page, Text } from "@wix/design-system";
import Subscription from "../models/subscription";
import SubscriptionContext from "../contexts/subscription-context";
import PageLoader from "./common/PageLoader";
import { showToast } from "@wix/dashboard-sdk";
import TrialExpiryAlert from "./trial-expiry-alert";
import { plans } from "../config/pricing";

interface PlanProps {
  id: number;
  productId: string;
  name: string;
  currency: string;
  price: number;
  features: string[];
  handleStartTrial: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleChoosePlan: (event: React.MouseEvent<HTMLButtonElement>) => void;
  numberOfTrialDays: number;
  wantsToUpgrade: boolean;
  isCurrentPlan: boolean;
}

const Plan: React.FC<PlanProps> = ({
  id,
  name,
  currency,
  price,
  features,
  handleStartTrial,
  handleChoosePlan,
  numberOfTrialDays,
  wantsToUpgrade = false,
  isCurrentPlan,
}) => {

  /*
  |--------------------------------------------------------------------------
  | Subscription details
  |--------------------------------------------------------------------------
  |
  */

  const { isTrialExpired, currentPlan, isTrialStarted, isPaid, isExpired } = useContext(SubscriptionContext);

  useEffect(() => {
    if (currentPlan) {
      let currentPlanId = null;
      plans.map((plan) => {
        if (plan.productId === currentPlan) {
          currentPlanId = plan.id;
        }
      });
    }
  }, [currentPlan]);

  return (
    <Card>
      <Card.Content>
        <Heading size="medium" style={{ display: "flex", justifyContent: "space-between" }}>
          {name}
          {isCurrentPlan && <Badge size="small">CURRENT</Badge>}
        </Heading>
        <Box marginTop="20px">
          <Text size="small">
            <ul>
              {features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </Text>
        </Box>
        <Box marginTop="30px" display={"flex"} placeItems={"center"} align={"space-between"}>
          <Heading size="medium">
            {currency}
            {price}
          </Heading>
          <Button
            size="small"
            onClick={isTrialExpired || wantsToUpgrade ? handleChoosePlan : handleStartTrial}
            disabled={isPaid && isCurrentPlan}
          >
            {/* Trial not started */}
            {!isPaid && !isTrialStarted && `Start ${numberOfTrialDays}-days free trial`}
            {/* Trial started but not trial expired */}
            {!isPaid && isTrialStarted && !isTrialExpired && 'Start paid plan'}
            {/* Trial started and trial expired */}
            {!isPaid && isTrialStarted && isTrialExpired && isCurrentPlan && 'Proceed to payment'}
            {!isPaid && isTrialStarted && isTrialExpired && !isCurrentPlan && 'Select plan'}
            {/* Paid */}
            {isPaid  && !isCurrentPlan && 'Select plan'}
            {isPaid  && isCurrentPlan && 'Current Selected'}
        
          </Button>
        </Box>
      </Card.Content>
    </Card>
  );
};

interface SubscriptionProps {
  wantsToUpgrade?: boolean;
}

const Subscribe: React.FC<SubscriptionProps> = ({ wantsToUpgrade = false }) => {
  /*
    |--------------------------------------------------------------------------
    | Is processing
    |--------------------------------------------------------------------------
    |
    */

  const [isProcessing, setIsProcessing] = useState(false);

  /*
    |--------------------------------------------------------------------------
    | Site details
    |--------------------------------------------------------------------------
    |
    */

  const { numberOfTrialDays, formattedTrialExpiryDate, isTrialExpired, isTrialStarted, currentPlan, isPaid } =
    useContext(SubscriptionContext);

  /*
    |--------------------------------------------------------------------------
    | Handle choose plan
    |--------------------------------------------------------------------------
    |
    */

  const handleChoosePlan = (productId: string) => {
    if (isProcessing) return;
    setIsProcessing(true);

    Subscription.getCheckoutUrl({ productId: productId })
      .then((response) => {
        window.location.href = response.data.checkoutUrl;
      })
      .catch((error) => setIsProcessing(false));
  };

  /*
    |--------------------------------------------------------------------------
    | Handle start trial
    |--------------------------------------------------------------------------
    |
    */

  const handleStartTrial = (productId: string) => {
    if (isProcessing) return;
    setIsProcessing(true);

    Subscription.startTrial({ productId: productId })
      .then((response) => {
        window.location.reload();
        showToast({
          type: "success",
          message: response.message,
        });
      })
      .catch((error) => setIsProcessing(false));
  };

  let expiryDate = formattedTrialExpiryDate ? formattedTrialExpiryDate.split("(") : [null];

  return (
    <>
      {isProcessing && <PageLoader />}
      <Page>
        <Page.Header
          title="Pricing Plan"
          subtitle={
            <Text size="small">
              Please select the plan that you feel is best suited for your business.
              {
                !isPaid &&
                <>
                  <br />
                  <Text weight="bold">
                    The duration of your free trial is charge-free. You won't be charged if you uninstall the app before{" "}
                    &nbsp;
                    {expiryDate[0] ? expiryDate[0] : "trial expiry"}
                  </Text>
                </>
              }
            </Text>
          }
        />
        <Page.Content>
          <TrialExpiryAlert />
          <Layout justifyItems="center" gap={"26px"}>
            {plans.map((plan) => (
              <Cell key={plan.id} span={3}>
                <Plan
                  id={plan.id}
                  productId={plan.productId}
                  name={plan.name}
                  currency={plan.currency}
                  price={plan.price}
                  features={plan.features}
                  handleStartTrial={() => handleStartTrial(plan.productId)}
                  handleChoosePlan={() => handleChoosePlan(plan.productId)}
                  numberOfTrialDays={numberOfTrialDays}
                  wantsToUpgrade={wantsToUpgrade}
                  isCurrentPlan={currentPlan === plan.productId}
                />
              </Cell>
            ))}
            <Cell span={12}>
              {
                (isTrialStarted && !isPaid) &&
                <Text secondary>
                  <center>
                    Your {numberOfTrialDays}-days free trial {isTrialExpired ? "expired" : "expires"} on{" "} {formattedTrialExpiryDate}.
                  </center>
                </Text>
              }
              {
                isPaid &&
                <Text size='small' secondary>
                  <center>
                  Your subscription is on a monthly plan and will renew automatically. A payment will be charged every
                  month.
                  </center>
                </Text>
              }
              
              {
                isTrialStarted &&
                <Text size="tiny">
                  <center>
                    *You  can either continue with the current plan or change to new plan.
                  </center>
                </Text>
              }
            </Cell>
          </Layout>
        </Page.Content>
      </Page>
    </>
  );
};

export default Subscribe;
